import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown, Minus, Shield, Info, ChevronRight } from 'lucide-react';
import { CreditScore } from '../../types/creditScore';
import { formatCreditScore, getRiskTierColor, getRiskTierLabel } from '../../utils/creditScoring';
interface CreditScoreCardProps {
  score: CreditScore;
  showDetails?: boolean;
  onViewDetails?: () => void;
}
export function CreditScoreCard({
  score,
  showDetails = false,
  onViewDetails
}: CreditScoreCardProps) {
  const scoreTrend = score.generalScore >= 700 ? 'up' : score.generalScore >= 600 ? 'stable' : 'down';
  const trendIcon = scoreTrend === 'up' ? TrendingUp : scoreTrend === 'down' ? TrendingDown : Minus;
  const TrendIcon = trendIcon;
  // Calculate score percentage for circular progress
  const scorePercentage = score.generalScore / 850 * 100;
  const circumference = 2 * Math.PI * 70; // radius = 70
  const strokeDashoffset = circumference - scorePercentage / 100 * circumference;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl shadow-xl overflow-hidden relative" data-id="element-720">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10" data-id="element-721">
        <div className="absolute inset-0" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} data-id="element-722"></div>
      </div>

      <div className="relative z-10 p-8" data-id="element-723">
        <div className="flex items-start justify-between mb-6" data-id="element-724">
          <div data-id="element-725">
            <div className="flex items-center space-x-2 mb-2" data-id="element-726">
              <Shield className="h-5 w-5 text-blue-200" data-id="element-727" />
              <h3 className="text-blue-100 text-sm font-medium uppercase tracking-wider" data-id="element-728">
                Your Credit Score
              </h3>
            </div>
            <p className="text-blue-200 text-xs" data-id="element-729">
              Updated {new Date(score.lastUpdated).toLocaleDateString()}
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-bold ${getRiskTierColor(score.riskTier)} border`} data-id="element-730">
            {getRiskTierLabel(score.riskTier)}
          </div>
        </div>

        <div className="flex items-center justify-between" data-id="element-731">
          {/* Circular Score Display */}
          <div className="relative" data-id="element-732">
            <svg className="transform -rotate-90" width="160" height="160" data-id="element-733">
              {/* Background circle */}
              <circle cx="80" cy="80" r="70" stroke="rgba(255, 255, 255, 0.2)" strokeWidth="12" fill="none" data-id="element-734" />
              {/* Progress circle */}
              <motion.circle cx="80" cy="80" r="70" stroke="white" strokeWidth="12" fill="none" strokeLinecap="round" initial={{
              strokeDashoffset: circumference
            }} animate={{
              strokeDashoffset
            }} transition={{
              duration: 1.5,
              ease: 'easeOut'
            }} style={{
              strokeDasharray: circumference
            }} data-id="element-735" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-736">
              <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              delay: 0.5,
              type: 'spring',
              stiffness: 200
            }} className="text-center" data-id="element-737">
                <div className="text-5xl font-bold text-white mb-1" data-id="element-738">
                  {formatCreditScore(score.generalScore)}
                </div>
                <div className="text-xs text-blue-200 uppercase tracking-wider" data-id="element-739">
                  out of 850
                </div>
              </motion.div>
            </div>
          </div>

          {/* Score Details */}
          <div className="flex-1 ml-8 space-y-4" data-id="element-740">
            {/* General Score */}
            <div data-id="element-741">
              <div className="flex items-center justify-between mb-2" data-id="element-742">
                <span className="text-sm text-blue-100" data-id="element-743">General Score</span>
                <span className="text-lg font-bold text-white" data-id="element-744">
                  {score.generalScore}
                </span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-745">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: `${score.generalScore / 850 * 100}%`
              }} transition={{
                duration: 1,
                delay: 0.3
              }} className="h-full bg-white rounded-full" data-id="element-746" />
              </div>
            </div>

            {/* R-Score */}
            <div data-id="element-747">
              <div className="flex items-center justify-between mb-2" data-id="element-748">
                <span className="text-sm text-blue-100" data-id="element-749">
                  Rent Score (R-Score)
                </span>
                <span className="text-lg font-bold text-white" data-id="element-750">
                  {score.rentScore}
                </span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden" data-id="element-751">
                <motion.div initial={{
                width: 0
              }} animate={{
                width: `${score.rentScore / 850 * 100}%`
              }} transition={{
                duration: 1,
                delay: 0.5
              }} className="h-full bg-green-400 rounded-full" data-id="element-752" />
              </div>
            </div>

            {/* Default Probability */}
            <div className="pt-4 border-t border-white/20" data-id="element-753">
              <div className="flex items-center justify-between" data-id="element-754">
                <span className="text-sm text-blue-100" data-id="element-755">Default Risk</span>
                <span className="text-sm font-bold text-white" data-id="element-756">
                  {score.defaultProbability.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Score Trend */}
        <div className="mt-6 pt-6 border-t border-white/20" data-id="element-757">
          <div className="flex items-center justify-between" data-id="element-758">
            <div className="flex items-center space-x-2" data-id="element-759">
              <TrendIcon className={`h-5 w-5 ${scoreTrend === 'up' ? 'text-green-300' : scoreTrend === 'down' ? 'text-red-300' : 'text-blue-200'}`} data-id="element-760" />
              <span className="text-sm text-blue-100" data-id="element-761">
                {scoreTrend === 'up' ? 'Score improving' : scoreTrend === 'down' ? 'Score declining' : 'Score stable'}
              </span>
            </div>
            {onViewDetails && <button onClick={onViewDetails} className="flex items-center space-x-1 text-sm text-white hover:text-blue-100 transition-colors" data-id="element-762">
                <span data-id="element-763">View Details</span>
                <ChevronRight className="h-4 w-4" data-id="element-764" />
              </button>}
          </div>
        </div>

        {/* Data Completeness */}
        <div className="mt-4 bg-white/10 rounded-lg p-3" data-id="element-765">
          <div className="flex items-center justify-between mb-2" data-id="element-766">
            <div className="flex items-center space-x-2" data-id="element-767">
              <Info className="h-4 w-4 text-blue-200" data-id="element-768" />
              <span className="text-xs text-blue-100" data-id="element-769">Data Completeness</span>
            </div>
            <span className="text-xs font-bold text-white" data-id="element-770">
              {score.dataCompleteness}%
            </span>
          </div>
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden" data-id="element-771">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: `${score.dataCompleteness}%`
          }} transition={{
            duration: 1,
            delay: 0.7
          }} className="h-full bg-yellow-400 rounded-full" data-id="element-772" />
          </div>
          {score.dataCompleteness < 100 && <p className="text-xs text-blue-200 mt-2" data-id="element-773">
              Complete your profile to improve score accuracy
            </p>}
        </div>
      </div>
    </motion.div>;
}